import { FormEvent, useEffect, useState } from "react";
import { Edit3, Plus, Send, Trash2 } from "lucide-react";
import { api, json } from "../../lib/api";
import { useAsync } from "../../lib/useAsync";
import { Field, Input, Select, Textarea } from "../../components/Form";
import {
  Empty,
  ErrorState,
  Failure,
  Loading,
  Success,
} from "../../components/State";

interface Contact {
  id?: string;
  purpose: string;
  type: string;
  label: string;
  value?: string;
  masked_value?: string;
  office_hours?: string;
  is_primary: boolean;
  is_public: boolean;
  sort_weight: number;
}
interface PortalContent {
  id?: string;
  section: string;
  code: string;
  title: string;
  subtitle?: string;
  body_markdown?: string;
  link_url?: string;
  link_label?: string;
  sort_weight: number;
  status?: string;
  version_status?: string;
}
interface PortalConfig {
  branding: {
    name: string;
    short_name: string;
    primary_color: string;
    secondary_color: string;
    support_text?: string;
    icp_text?: string;
  };
  park: {
    name: string;
    short_name: string;
    description?: string;
    address: string;
  };
  contacts: Contact[];
  contents: PortalContent[];
}

const blankContent: PortalContent = {
  section: "hero",
  code: "hero_main",
  title: "",
  subtitle: "",
  body_markdown: "",
  link_url: "",
  link_label: "",
  sort_weight: 0,
};
export function AdminPortalSettingsPage() {
  const result = useAsync(() => api<PortalConfig>("/admin/portal-config"), []);
  const [form, setForm] = useState<PortalConfig>();
  const [editing, setEditing] = useState<PortalContent>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (result.data) setForm(structuredClone(result.data));
  }, [result.data]);
  if (result.loading || !form)
    return result.error ? (
      <ErrorState error={result.error} retry={result.retry} />
    ) : (
      <Loading />
    );
  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const updated = await api<PortalConfig>("/admin/portal-config", {
        method: "PATCH",
        body: json({
          branding: {
            name: form.branding.name,
            short_name: form.branding.short_name,
            primary_color: form.branding.primary_color,
            secondary_color: form.branding.secondary_color,
            support_text: form.branding.support_text || "",
            icp_text: form.branding.icp_text || "",
          },
          park: form.park,
          contacts: form.contacts.map((contact) => ({
            id: contact.id,
            purpose: contact.purpose,
            type: contact.type,
            label: contact.label,
            value: contact.value || "",
            office_hours: contact.office_hours || "",
            is_primary: contact.is_primary,
            is_public: contact.is_public,
            sort_weight: contact.sort_weight,
          })),
        }),
      });
      setForm(updated);
      setMessage("门户基础配置已保存");
    } catch (value) {
      setError(value instanceof Error ? value.message : "保存失败");
    } finally {
      setBusy(false);
    }
  };
  const saveContent = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    setBusy(true);
    setError("");
    try {
      await api(
        editing.id
          ? `/admin/portal-contents/${editing.id}`
          : "/admin/portal-contents",
        { method: editing.id ? "PUT" : "POST", body: json(editing) },
      );
      setEditing(undefined);
      setMessage("门户内容草稿已保存");
      await result.retry();
    } catch (value) {
      setError(value instanceof Error ? value.message : "保存失败");
    } finally {
      setBusy(false);
    }
  };
  const action = async (item: PortalContent, kind: "publish" | "archive") => {
    setError("");
    try {
      await api(
        `/admin/portal-contents/${item.id}${kind === "publish" ? "/publish" : ""}`,
        {
          method: kind === "publish" ? "POST" : "DELETE",
          ...(kind === "publish" ? { body: json({}) } : {}),
        },
      );
      setMessage(kind === "publish" ? "门户内容已发布" : "门户内容已归档");
      await result.retry();
    } catch (value) {
      setError(value instanceof Error ? value.message : "操作失败");
    }
  };
  return (
    <div>
      <header className="admin-page-header">
        <div>
          <span>PORTAL SETTINGS</span>
          <h1>园区门户</h1>
          <p>品牌、园区资料、联系方式和门户内容版本。</p>
        </div>
      </header>
      {message && <Success>{message}</Success>}
      {error && <Failure>{error}</Failure>}
      <form onSubmit={save}>
        <section className="admin-panel detail-section">
          <header>
            <div>
              <span>BRAND</span>
              <h2>品牌与园区资料</h2>
            </div>
          </header>
          <div className="form-grid">
            <Field label="品牌名称" required>
              <Input
                required
                maxLength={200}
                value={form.branding.name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    branding: { ...form.branding, name: e.target.value },
                  })
                }
              />
            </Field>
            <Field label="品牌简称" required>
              <Input
                required
                maxLength={100}
                value={form.branding.short_name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    branding: { ...form.branding, short_name: e.target.value },
                  })
                }
              />
            </Field>
            <Field label="主色">
              <Input
                type="color"
                value={form.branding.primary_color}
                onChange={(e) =>
                  setForm({
                    ...form,
                    branding: {
                      ...form.branding,
                      primary_color: e.target.value,
                    },
                  })
                }
              />
            </Field>
            <Field label="辅助色">
              <Input
                type="color"
                value={form.branding.secondary_color}
                onChange={(e) =>
                  setForm({
                    ...form,
                    branding: {
                      ...form.branding,
                      secondary_color: e.target.value,
                    },
                  })
                }
              />
            </Field>
            <Field label="园区名称" required>
              <Input
                required
                value={form.park.name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    park: { ...form.park, name: e.target.value },
                  })
                }
              />
            </Field>
            <Field label="园区简称" required>
              <Input
                required
                value={form.park.short_name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    park: { ...form.park, short_name: e.target.value },
                  })
                }
              />
            </Field>
            <Field label="园区地址" required>
              <Input
                required
                value={form.park.address}
                onChange={(e) =>
                  setForm({
                    ...form,
                    park: { ...form.park, address: e.target.value },
                  })
                }
              />
            </Field>
            <Field label="ICP备案展示">
              <Input
                value={form.branding.icp_text || ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    branding: { ...form.branding, icp_text: e.target.value },
                  })
                }
              />
            </Field>
            <Field label="园区简介">
              <Textarea
                rows={3}
                value={form.park.description || ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    park: { ...form.park, description: e.target.value },
                  })
                }
              />
            </Field>
            <Field label="平台说明">
              <Textarea
                rows={3}
                value={form.branding.support_text || ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    branding: {
                      ...form.branding,
                      support_text: e.target.value,
                    },
                  })
                }
              />
            </Field>
          </div>
        </section>
        <section className="admin-panel detail-section">
          <header>
            <div>
              <span>CONTACTS</span>
              <h2>公开联系方式</h2>
            </div>
            <button
              type="button"
              className="secondary-button compact"
              onClick={() =>
                setForm({
                  ...form,
                  contacts: [
                    ...form.contacts,
                    {
                      purpose: "general",
                      type: "phone",
                      label: "服务电话",
                      value: "",
                      office_hours: "",
                      is_primary: false,
                      is_public: true,
                      sort_weight: 0,
                    },
                  ],
                })
              }
            >
              <Plus size={15} />
              添加
            </button>
          </header>
          {!form.contacts.length ? (
            <Empty title="暂无联系方式" />
          ) : (
            <div className="settings-list">
              {form.contacts.map((item, index) => (
                <div className="settings-row" key={item.id || index}>
                  <Select
                    aria-label="用途"
                    value={item.purpose}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        contacts: form.contacts.map((x, i) =>
                          i === index ? { ...x, purpose: e.target.value } : x,
                        ),
                      })
                    }
                  >
                    <option value="general">综合咨询</option>
                    <option value="admission">入驻咨询</option>
                    <option value="policy">政策咨询</option>
                    <option value="support">技术支持</option>
                    <option value="complaint">投诉建议</option>
                  </Select>
                  <Select
                    aria-label="类型"
                    value={item.type}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        contacts: form.contacts.map((x, i) =>
                          i === index ? { ...x, type: e.target.value } : x,
                        ),
                      })
                    }
                  >
                    <option value="phone">电话</option>
                    <option value="mobile">手机</option>
                    <option value="email">邮箱</option>
                    <option value="wechat">微信</option>
                    <option value="address">地址</option>
                  </Select>
                  <Input
                    aria-label="标签"
                    value={item.label}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        contacts: form.contacts.map((x, i) =>
                          i === index ? { ...x, label: e.target.value } : x,
                        ),
                      })
                    }
                  />
                  <Input
                    aria-label="联系值"
                    placeholder={item.masked_value || "联系值"}
                    value={item.value || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        contacts: form.contacts.map((x, i) =>
                          i === index ? { ...x, value: e.target.value } : x,
                        ),
                      })
                    }
                  />
                  <label>
                    <input
                      type="checkbox"
                      checked={item.is_public}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          contacts: form.contacts.map((x, i) =>
                            i === index
                              ? { ...x, is_public: e.target.checked }
                              : x,
                          ),
                        })
                      }
                    />
                    公开
                  </label>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label="删除"
                    onClick={() =>
                      setForm({
                        ...form,
                        contacts: form.contacts.filter((_, i) => i !== index),
                      })
                    }
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
        <button className="primary-button" disabled={busy}>
          {busy ? "保存中..." : "保存基础配置"}
        </button>
      </form>
      <section className="admin-panel detail-section portal-content-section">
        <header>
          <div>
            <span>CONTENT VERSIONS</span>
            <h2>门户内容</h2>
          </div>
          <button
            className="secondary-button compact"
            onClick={() => setEditing({ ...blankContent })}
          >
            <Plus size={15} />
            新建内容
          </button>
        </header>
        {!form.contents.length ? (
          <Empty title="暂无门户内容" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>区块 / 标识</th>
                  <th>标题</th>
                  <th>版本状态</th>
                  <th>内容状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {form.contents.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.section}
                      <small>{item.code}</small>
                    </td>
                    <td>{item.title}</td>
                    <td>{item.version_status}</td>
                    <td>{item.status}</td>
                    <td>
                      <div className="row-actions">
                        <button onClick={() => setEditing({ ...item })}>
                          <Edit3 size={14} />
                          编辑
                        </button>
                        {item.version_status === "draft" && (
                          <button onClick={() => action(item, "publish")}>
                            <Send size={14} />
                            发布
                          </button>
                        )}
                        <button
                          className="danger-text"
                          onClick={() => action(item, "archive")}
                        >
                          <Trash2 size={14} />
                          归档
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {editing && (
        <div className="modal-layer">
          <button
            className="modal-backdrop"
            aria-label="关闭"
            onClick={() => setEditing(undefined)}
          />
          <form className="modal-card wide-modal" onSubmit={saveContent}>
            <header>
              <h2>{editing.id ? "编辑门户内容" : "新建门户内容"}</h2>
              <button type="button" onClick={() => setEditing(undefined)}>
                ×
              </button>
            </header>
            <div className="form-grid">
              <Field label="区块" required>
                <Select
                  value={editing.section}
                  onChange={(e) =>
                    setEditing({ ...editing, section: e.target.value })
                  }
                >
                  <option value="hero">首屏</option>
                  <option value="value_props">核心价值</option>
                  <option value="process_steps">办理流程</option>
                  <option value="admission_benefits">入驻权益</option>
                  <option value="service_highlights">服务亮点</option>
                  <option value="cta">行动入口</option>
                  <option value="faq">常见问题</option>
                  <option value="contact_block">联系信息</option>
                  <option value="footer">页脚</option>
                </Select>
              </Field>
              <Field label="稳定标识" required>
                <Input
                  required
                  pattern="[a-z][a-z0-9_]{1,49}"
                  value={editing.code}
                  onChange={(e) =>
                    setEditing({ ...editing, code: e.target.value })
                  }
                />
              </Field>
              <Field label="标题" required>
                <Input
                  required
                  maxLength={200}
                  value={editing.title}
                  onChange={(e) =>
                    setEditing({ ...editing, title: e.target.value })
                  }
                />
              </Field>
              <Field label="副标题">
                <Input
                  maxLength={200}
                  value={editing.subtitle || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, subtitle: e.target.value })
                  }
                />
              </Field>
              <Field label="正文 Markdown">
                <Textarea
                  rows={10}
                  maxLength={20000}
                  value={editing.body_markdown || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, body_markdown: e.target.value })
                  }
                />
              </Field>
              <Field label="链接">
                <Input
                  value={editing.link_url || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, link_url: e.target.value })
                  }
                />
              </Field>
              <Field label="链接文案">
                <Input
                  value={editing.link_label || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, link_label: e.target.value })
                  }
                />
              </Field>
              <Field label="排序权重">
                <Input
                  type="number"
                  value={editing.sort_weight}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      sort_weight: Number(e.target.value),
                    })
                  }
                />
              </Field>
            </div>
            <button className="primary-button" disabled={busy}>
              保存草稿
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
